import { ref } from 'vue';

import { eqsApi } from '@/api/eqs';

export type CalendarKind = 'holiday' | 'workday';
export interface CalendarEntry { day: string; kind: CalendarKind; name: string }

/*
  工作日历（1006）。排期时把日期填到周末 / 法定假日上，界面要说一声——
  线上 1230 条在跑活动里 204 条（17%）计划完成日落在周六周日。
  判据和后端 calendar/work-calendar.ts 同一条：表里标 workday 的上班、标 holiday 的放假、
  没标的按周一到周五。表里只有假日和调休日，周末前端自己判，不用把 104 个周末也拉下来。
  整个页面共用一份缓存：拉一次当年 + 明年，所有日期框都从这里问。
*/
const entries = ref<Map<string, CalendarEntry>>(new Map());
const loadedRanges = new Set<string>();
let pending: Promise<void> | null = null;

async function ensureLoaded(year: number): Promise<void> {
  const key = String(year);
  if (loadedRanges.has(key)) return;
  if (pending) await pending;
  if (loadedRanges.has(key)) return;
  pending = (async () => {
    try {
      // 测试里 eqsApi 是替身，没这个方法就只按周末判
      if (typeof eqsApi.calendarEntries !== 'function') { loadedRanges.add(key); return; }
      const payload = await eqsApi.calendarEntries(`${year}-01-01`, `${year + 1}-12-31`);
      const next = new Map(entries.value);
      for (const entry of payload.entries ?? []) next.set(entry.day, entry);
      entries.value = next;
      loadedRanges.add(key);
      loadedRanges.add(String(year + 1));
    } catch {
      // 拉不到日历就只按周末判；提示少一句比整个抽屉报错好
    } finally {
      pending = null;
    }
  })();
  await pending;
}

/** 1 = 周一 … 7 = 周日 */
function isoWeekday(day: string): number {
  const [year, month, date] = day.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, date)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

const WEEKDAY_NAMES = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日'];

export function useWorkCalendar() {
  /** 这天不上班的原因：'周六' / '周日' / 节日名；上班日（含调休）返回 null */
  function restReason(day: string | null | undefined): string | null {
    const key = String(day ?? '').trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(key)) return null;
    void ensureLoaded(Number(key.slice(0, 4)));
    const entry = entries.value.get(key);
    if (entry) return entry.kind === 'holiday' ? entry.name : null;
    const weekday = isoWeekday(key);
    return weekday >= 6 ? WEEKDAY_NAMES[weekday] : null;
  }

  /** 给日期框下面那行小字：「10-02 是国庆节，当天没人上班」；上班日返回空串 */
  function hint(day: string | null | undefined, label = '这天'): string {
    const reason = restReason(day);
    if (!reason) return '';
    return `${label}是${reason}，当天不上班，到期和催办会落到下一个工作日`;
  }

  return { restReason, hint, ensureLoaded, entries };
}
